import type { ISceneManager, IAudioEngine, ISaveAdapter } from "./types";
import { SceneManager } from "./SceneManager";
import { HowlerAudioEngine } from "./audio/HowlerAudioEngine";
import { SaveManager } from "./save/SaveManager";
import { LocalStorageAdapter } from "./save/LocalStorageAdapter";
import { MemoryAdapter } from "./save/MemoryAdapter";
import { InputManager } from "./input/InputManager";
import { I18n } from "./i18n/I18n";
import { PromoFetcher } from "./net/PromoFetcher";
import { InstallPromptHelper } from "./pwa/InstallPrompt";

function pickSaveAdapter(): ISaveAdapter {
  try {
    const probe = "__ccd_save_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return new LocalStorageAdapter();
  } catch {
    console.warn("[save] localStorage unavailable; falling back to in-memory adapter");
    return new MemoryAdapter();
  }
}

export interface GameOpts {
  container: HTMLElement;
}

/**
 * Root game object. Owns engine services and drives the scene loop.
 * Game-agnostic — knows nothing about specific games.
 * Litmus test: this class must run Solitaire, Pac-Man, or POPPHIN Ocean Match unchanged.
 */
export class Game {
  readonly scenes: ISceneManager;
  readonly audio: IAudioEngine;
  readonly save: SaveManager;
  readonly input: InputManager;
  readonly i18n: I18n;
  readonly promos: PromoFetcher;
  readonly install: InstallPromptHelper;

  private readonly container: HTMLElement;
  private booted = false;
  private visibilityHandler?: () => void;
  private beforeUnloadHandler?: () => void;

  constructor(opts: GameOpts) {
    this.container = opts.container;
    this.scenes = new SceneManager(this.container);
    this.audio = new HowlerAudioEngine();
    this.save = new SaveManager(pickSaveAdapter());
    this.input = new InputManager();
    this.i18n = new I18n();
    this.promos = new PromoFetcher();
    this.install = new InstallPromptHelper();
  }

  boot(): void {
    if (this.booted) return;
    this.booted = true;

    this.visibilityHandler = () => {
      if (document.hidden) this.audio.pauseAll();
      else this.audio.resumeAll();
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);

    this.beforeUnloadHandler = () => this.save.flush();
    window.addEventListener("beforeunload", this.beforeUnloadHandler);

    this.install.watch();
  }

  shutdown(): void {
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
    if (this.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    }
    this.install.unwatch();
    this.audio.stopMusic();
    if (typeof (this.input as unknown as { dispose?: () => void }).dispose === "function") {
      (this.input as unknown as { dispose: () => void }).dispose();
    }
    if (typeof (this.audio as unknown as { dispose?: () => void }).dispose === "function") {
      (this.audio as unknown as { dispose: () => void }).dispose();
    }
    this.booted = false;
  }
}
